import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useContext, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import KeysContext from "contexts/KeysContext";
import NameCell from "./Cells/NameCell";
import StatusCell from "./Cells/StatusCell";
import tagsService from "api/tagsService";
import statusService from "api/statusService";
import TagCell from "./Cells/TagCell";
import { IAttachment } from "dto/IAttachment";
import MenuCell from "./Cells/MenuCell";
import PreviewModal from "components/PreviewModal";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import { plGridLocales } from "utils/muiGridLocales";
import Toolbar from "components/CaseDossier/Toolbar";
import IssuesContext from "contexts/IssuesContext";
import { IIssue } from "dto/Helpdesk/IIssue";
import SenderCell from "./Cells/SenderCell";
import ChatModal from "components/ChatModal";
import { useTranslation } from "react-i18next";
import useGridRowSelection from "hooks/useGridRowSelection";

const MessagesRegister: FC<Props> = ({ searchText }) => {
  const { t } = useTranslation();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const { issues, setIssues } = useContext(IssuesContext);
  const { keys } = useContext(KeysContext);

  const [previewItem] = useState<IAttachment | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const {
    data,
    fetchNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    ["issues", searchText],
    async ({ pageParam = "" }) => {
      const query = new URLSearchParams(window.location.search);

      query.append("Page", pageParam !== "" ? pageParam : "0");

      if (searchText.length) {
        query.append("Text", searchText);
      }

      const res = await axiosInstance.get(`/issues?${query}`);
      return res.data;
    },
    {
      enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.Page + 1;
        }
        return undefined;
      },
    }
  );

  const [isInitialized, setIsInitialized] = useState(false);
  const [chatId, setChatId] = useState<string>(null);

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat()
    );

    setIssues(packs.flat());
    setIsInitialized(true);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    setIssues([]);
    setIsInitialized(false);

    setTimeout(() => {
      refetch();
    }, 500);
  }, []);

  const onDownload = async () => {
    // setDownloadFileName(attachment.FileName);
    // create(pack.Id, attachment, pack.Kind, true);

    return;
  };

  const rows = issues.map((c) => {
    //@ts-ignore
    c.id = c.Id;
    return c;
  });
  rowsRef.current = rows;

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        {issues.length === 0 && !isLoading && isInitialized && (
          <div className={styles.empty}>
            <img src={Search} />
            <div>{t("emptyStates.noResults")}</div>
          </div>
        )}

        {(issues?.length > 0 || isLoading || !isInitialized) && (
          <DataGridPremium
            sx={{
              border: 0,
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
            }}
            localeText={plGridLocales}
            slots={{ toolbar: Toolbar }}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            loading={!isInitialized || isLoading}
            columnHeaderHeight={30}
            onRowsScrollEnd={() => {
              fetchNextPage();
            }}
            disableRowSelectionOnClick
            rowSelectionModel={selectedRowIds}
            onRowClick={handleRowClick}
            onRowDoubleClick={(a) => {
              const issue = a.row as IIssue;
              //              navigate(`/helpdesk/issues/${issue.ChatId}`);
              setChatId(issue.ChatId);
            }}
            columns={[
              {
                field: "CreationDate",
                width: 140,
                headerName: t("helpdesk.dateReported"),
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("DD.MM.yyyy HH:mm");
                  }
                  return null;
                },
              },
              {
                field: "Sender",
                minWidth: 140,
                flex: 1,
                headerName: t("helpdesk.reporter"),
                cellClassName: `${styles.senderCell}`,
                valueGetter: (value, row) => row?.Sender,
                renderCell: (params: GridRenderCellParams<IIssue, string>) =>
                  params.rowNode.type !== "group" ? (
                    <SenderCell element={params.row}></SenderCell>
                  ) : (
                    <div>{params.value}</div>
                  ),
              },
              {
                field: "Label",
                minWidth: 280,
                flex: 1,
                headerName: t("common.name"),
                cellClassName: `${styles.nameCell}`,
                valueGetter: (value, row) => row?.Title,
                renderCell: (params: GridRenderCellParams<IIssue, string>) =>
                  params.rowNode.type !== "group" ? (
                    <NameCell element={params.row}></NameCell>
                  ) : (
                    <div>{params.value}</div>
                  ),
              },
              {
                field: "Status",
                minWidth: 140,
                headerName: t("common.status"),
                headerAlign: "center",
                align: "center",
                cellClassName: `${styles.statusCell}`,
                renderCell: (params: GridRenderCellParams<IIssue, string>) => (
                  <StatusCell
                    element={params.row}
                    statuses={statuses}
                  ></StatusCell>
                ),
              },
              {
                field: "TagIds",
                minWidth: 140,
                headerName: t("forms.label"),
                headerAlign: "center",
                align: "center",
                cellClassName: `${styles.nameCell}`,
                renderCell: (params: GridRenderCellParams<IIssue, string>) => (
                  <TagCell element={params.row} tags={tags}></TagCell>
                ),
              },

              {
                field: "menu",
                width: 40,
                headerName: t("common.menu"),
                disableReorder: true,
                sortable: false,
                resizable: false,
                hideable: false,
                renderHeader: () => <div></div>,
                renderCell: (params: GridRenderCellParams<IIssue, string>) => (
                  <MenuCell
                    element={params.row}
                    onDownload={onDownload}
                    keys={keys}
                  ></MenuCell>
                ),
              },
            ]}
            hideFooter={true}
            rows={rows}
            disableColumnFilter={true}
            disableAggregation={true}
          />
        )}
      </div>

      {showPreview && previewItem && (
        <ModalLayout
          dock={DockPosition.FULLSCREEN_CENTER}
          customY={10}
          isAnimation={false}
          ignore={false}
          toggle={() => setShowPreview(false)}
          showBg={true}
        >
          <PreviewModal
            itemId={previewItem.Id}
            itemKind={previewItem.Kind}
            fileUri={previewItem.FileUri}
            encryptionKeyId={previewItem.EncryptionKeyId}
            fileName={previewItem.FileName}
            toggle={() => setShowPreview(false)}
            refetch={() => refetch()}
          ></PreviewModal>
        </ModalLayout>
      )}
      {chatId && (
        <div className={styles.modalBg}>
          <ChatModal chatId={chatId} toggle={() => setChatId(null)}></ChatModal>
        </div>
      )}
    </div>
  );
};

export default MessagesRegister;
